import { motion } from "framer-motion";
import { Mic, Sparkles, Check, Rocket } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const STEPS = [
  {
    number: "01",
    title: "Speak Your Vision",
    description: "Simply tell YASNO what you want. 'Create a LinkedIn campaign about AI for my tech startup, posting 3 times a week for a month.'",
    icon: Mic
  },
  {
    number: "02",
    title: "AI Creates Everything",
    description: "Our AI generates strategic topics, writes platform-optimized scripts, creates stunning visuals, and even produces videos.",
    icon: Sparkles
  },
  {
    number: "03",
    title: "Review & Approve",
    description: "Preview all content in your dashboard. Edit if needed, or approve with one click. The AI learns from your preferences.",
    icon: Check
  },
  {
    number: "04",
    title: "Autopilot Publishing",
    description: "Content posts automatically to all platforms at optimal times. Track performance and let AI optimize future campaigns.",
    icon: Rocket
  }
];

/**
 * HowItWorksSection Component - NO ITALICS, with exchanged video
 */
export default function HowItWorksSection({ onShowVideo }) {
  return (
    <section id="how-it-works" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            How It Works
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-4" data-testid="how-it-works-title">
            From Voice to Viral <span className="gradient-text">in 4 Steps</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            No learning curve. No complex setup. Just speak and watch the magic happen.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {STEPS.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="relative text-center sm:text-left"
            >
              <div className="relative mb-4 sm:mb-6 inline-block">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <step.icon className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                </div>
                <span className="absolute -top-2 -right-2 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary text-primary-foreground text-xs sm:text-sm font-bold flex items-center justify-center">
                  {step.number}
                </span>
              </div>
              
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 tracking-tight">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Video Section - Shows Marketing video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          {/* Video Player - WITH CONTROLS */}
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-video mb-4 sm:mb-6">
            <video
              className="w-full h-full object-contain"
              controls
              playsInline
              poster="/video-poster.jpg"
            >
              <source src="/yasno-marketing.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          
          {/* Video CTA - Opens voice chat modal */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
            <div className="text-center sm:text-left">
              <h3 className="text-lg sm:text-xl font-semibold tracking-tight">See YASNO in Action</h3>
              <p className="text-sm text-muted-foreground">Watch a live demo of AI marketing automation</p>
            </div>
            <Button onClick={onShowVideo} className="w-full sm:w-auto rounded-full bg-primary hover:bg-primary/90 neon-glow" data-testid="how-it-works-video-btn">
              <Mic className="w-4 h-4 mr-2" />
              Try It Yourself
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
